import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Grid,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text
} from '@chakra-ui/react'
import type { InsurerDiscount, SimpleItem } from 'BrokerTsLib/src/dtos'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'
import * as yup from 'yup'

import ErrorSummary from '@/components/ErrorSummary'
import { FormLabel } from '@/components/FormLabel'
import { InputGroup } from '@/components/InputGroup'
import CompaniesSelection from '@/containers/forms/CompaniesSelection'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { FormSection } from '@/containers/forms/FormSection'
import { usePartner } from '@/context/PartnerContext'
import type { NewPoleasImport } from '@/dtos/PoleasImport'
import { useGetAllInsurersAndDiscounts, useNewPoleasImport } from '@/hooks/usePoleas'

import { usePoleasImportContext } from '../../../context/PoleasImportProvider'

export const schema = yup.object().shape(
  {
    name: yup.string().required('Vyplňte název importu'),
    file: yup.mixed().required('Vyberte importní soubor')
  }
) as any

const importTypes: SimpleItem[] = [
  {
    id: 1,
    name: 'UCL'
  },
  {
    id: 2,
    name: 'RCI'
  },
  {
    id: 3,
    name: 'UCL + RCI'
  },
]

const importDefaults: NewPoleasImport = {
  name: '',
  type: undefined,
  insurers: [],
  insurerDiscounts: []
}

export type FormSectionBaseProps = {
  sectionTitle: string
  sectionNumber: number
}


export const NewImportForm = ({ sectionNumber, sectionTitle }: FormSectionBaseProps) => {
  const { currentPartner } = usePartner()

  const methods = useForm<NewPoleasImport>({
    defaultValues: importDefaults,
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset,
    setError,
    register,
    watch,
    clearErrors,
    trigger
  } = methods

  const { data: dataInsurersAndDiscounts } = useGetAllInsurersAndDiscounts()
  const insurersDiscounts = dataInsurersAndDiscounts?.data ?? []

  useEffect(() => {
    if ((insurersDiscounts?.length ?? 0) > 0 && getValues('insurers')?.length == 0)
      reset({
        ...getValues(),
        insurers: [...insurersDiscounts?.map(x => x.insurerId) ?? []]
      })
  }, [insurersDiscounts?.length])

  const watchedType = watch('type')
  const watchedFile = watch('file')
  const insurers = watch('insurers')
  const formDiscounts = watch('insurerDiscounts')

  useEffect(() => {
    const newDiscounts: InsurerDiscount[] = []
    for (let index = 0; index < insurers.length; ++index) {
      const d1 = formDiscounts.find(fd => fd.insurerId == insurers[index])
      if (d1) {
        newDiscounts.push(d1)
      }
      else {
        const d2 = insurersDiscounts?.find(id => id.insurerId == insurers[index])
        if (d2) newDiscounts.push(d2)
      }
    }
    setValue('insurerDiscounts', newDiscounts)
  }, [insurers, insurersDiscounts])

  const { mutateAsync: saveNewImport } = useNewPoleasImport()

  const { getPreviewOffers, importInProgress, previewCallInProgress, forceReloadImportForm, setImportInProgress } = usePoleasImportContext()

  useEffect(() => {
    if (forceReloadImportForm) {
      reset()
    }
  }, [forceReloadImportForm])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0]

      if (!getValues('name')) {
        setValue('name', file.name.replace('import_', '').split('.').slice(0, -1).join('.'))
        trigger('name')
      }

      if (!getValues('type')) {
        const ucl = file.name.toLocaleLowerCase().includes('ucl')
        const rci = file.name.toLocaleLowerCase().includes('rci')

        if (ucl && rci) setValue('type', 3)
        else if (rci) setValue('type', 2)
        else if (ucl) setValue('type', 1)
      }

      setValue('file', file)
    }
  }

  const submitForm = handleSubmit((data) => {
    setImportInProgress(true)
    clearErrors()
    return saveNewImport(data)
      .then((res) => {
        getPreviewOffers(res.data.importId, res.data.offerCount)
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
      .finally(() => setImportInProgress(false))
  })

  return (
    <FormSection
      number={sectionNumber}
      sectionTitle={sectionTitle}
      footer={
        <Stack w={'100%'} gap={16}>
          <Flex sx={{ justifyContent: 'center' }}>
            <Button
              colorScheme="green"
              isLoading={importInProgress || previewCallInProgress}
              isDisabled={!getValues('file')}
              onClick={submitForm}
            >
              Zobrazit náhled importu
            </Button>

          </Flex>
          <ErrorSummary errors={errors} />
        </Stack>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={submitForm}>
          <FormBody>
            <Grid rowGap={16} w='100%'>
              <CompaniesSelection
                allInsurers={insurersDiscounts.map(x => ({ id: x.insurerId, name: x.insurerName }))}
                allInsurersDiscounts={insurersDiscounts}
                insurersPath={'insurers'}
                insurersDiscountsPath='insurerDiscounts'
                columnsCount={2}
              />

              <FormRow>
                <FormControl isInvalid={!!errors.file}>
                  <FormLabel>
                    Importní soubor
                  </FormLabel>

                  <FormHelperText>
                    Vyberte soubor s importními daty ve formátu excel XLSX.
                  </FormHelperText>

                  <Box sx={{ display: 'grid', gap: 2 }}>
                    <Button as="label">
                      <Input
                        accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                        sx={{ width: 1, height: 1, padding: 0, margin: 0, opacity: 0.1 }}
                        onChange={handleFileChange}
                        type="file"
                      />
                      Vyberte XLSX Soubor
                    </Button>
                    {watchedFile &&
                                            <Text>{watchedFile.name}</Text>
                    }
                  </Box>

                  <FormErrorMessage>
                    {errors.file && errors?.file.message}
                  </FormErrorMessage>
                </FormControl>

                {currentPartner?.partner === 'Jap' &&
                                    <FormControl isInvalid={!!errors.type}>
                                      <FormLabel>
                                        Typ importu
                                      </FormLabel>

                                      <RadioGroup
                                        value={watchedType?.toString()}
                                        onChange={(value) => setValue('type', parseInt(value))}
                                      >
                                        <Stack spacing={6}>
                                          {importTypes.map((item) => (
                                            <Radio value={item.id.toString()} key={item.id}>
                                              {item.name}
                                            </Radio>
                                          ))}
                                        </Stack>
                                      </RadioGroup>

                                      {errors.type && <FormErrorMessage>{errors.type.message}</FormErrorMessage>}
                                    </FormControl>
                }
              </FormRow>

              <FormRow itemsCount={1}>
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel>
                    Název importu
                  </FormLabel>

                  <FormHelperText>
                    Zadejte název importu podle kterého bude dohledatelný.
                  </FormHelperText>

                  <InputGroup
                    errors={errors.name}
                    {...register('name')}
                  />
                </FormControl>
              </FormRow>
            </Grid>
          </FormBody>
        </form>
      </FormProvider>
    </FormSection >
  )
}